/**
 * Fetches the content behind an asset URL.
 *
 * Three URL shapes are supported:
 *
 * 1. **App API URLs** (contain `/agents/files`): the asset server responds to
 *    `?redirect=false` with JSON `{ location }` — the real presigned URL.
 *    This function resolves through that indirection.
 *
 * 2. **App API streaming URLs** (`/agents/files?...&stream=true`): already
 *    proxy the bytes same-origin with the correct Content-Type, so they're
 *    fetched directly. (`stream=true` and `redirect=false` are mutually
 *    exclusive, so path (1)'s indirection can't be used here.)
 *
 * 3. **Direct URLs** (e.g. presigned S3 URLs from the Lambda render's
 *    `assetMap`): fetched as-is. Appending extra query parameters to a
 *    presigned URL invalidates the AWS signature.
 */
export async function fetchAssetContent(assetUrl: string): Promise<Response> {
  if (!assetUrl.includes("/agents/files")) {
    const res = await fetch(assetUrl);
    if (!res.ok) {
      throw new Error(`Failed to fetch asset content: ${res.status}`);
    }
    return res;
  }

  if (assetUrl.includes("stream=true")) {
    const res = await fetch(assetUrl, { credentials: "include" });
    if (!res.ok) {
      throw new Error(`Failed to fetch asset content: ${res.status}`);
    }
    return res;
  }

  const separator = assetUrl.includes("?") ? "&" : "?";
  const presignedRes = await fetch(`${assetUrl}${separator}redirect=false`, {
    credentials: "include",
  });
  if (!presignedRes.ok) {
    throw new Error(`Failed to resolve asset: ${presignedRes.status}`);
  }

  const { location } = (await presignedRes.json()) as { location: string };

  const contentRes = await fetch(location);
  if (!contentRes.ok) {
    throw new Error(`Failed to fetch asset content: ${contentRes.status}`);
  }

  return contentRes;
}
